import { useRef, useState, type ReactNode } from "react";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import type { CustomerBasketTotals } from "@shared-core/data/customer-basket-types";
import { cn } from "../../../app/components/primitives/utils";
import {
  OrderSummaryBar,
  OrderSummaryPanel,
} from "../../customer-pages/OrderSummaryPanel";
import {
  SUMMARY_BESIDE_PAGE,
  useMediaQuery,
} from "../../customer-pages/useMediaQuery";
import { BasketTotals } from "./BasketTotals";

// The money area of the basket (the checkout lays out its own): the order page's own summary
// (`OrderSummaryPanel` / `OrderSummaryBar` and the shared `BottomSheet`, #2686), shown once with the lines left to
// the page. A sticky panel beside the page from `lg` up; a slim fixed bar and a short sheet below it.
//
// What is the basket's own, passed in rather than copied: the totals block (the server's goods, delivery, VAT and
// total, where an order page's is one ex VAT / inc VAT pair worked out from two figures) and the figure the bar
// shows. Everything else is the shared pieces'.

export interface BasketSummaryProps {
  /** The panel's accessible name, e.g. "Your total and checking out". */
  label: string;
  /** The sheet's title on a phone. */
  sheetTitle: string;
  totals: CustomerBasketTotals;
  /** What the page has to say about the money in one line (why Check out waits, where the payment stands). */
  state?: { text: string; isOwed: boolean } | null;
  /** Notes and actions under the totals, drawn identically in the panel and in the sheet. */
  children?: ReactNode;
  /** The bar's own button: one tap does it. Null: the bar has no button (nothing to do from here). */
  barAction: {
    label: string;
    onPress: () => void;
    disabled?: boolean;
  } | null;
}

/**
 * The page with its summary. From `lg` up: two columns, the lines on the left and the summary panel on the right,
 * wide enough to hold real controls (about 40% of the content, never under 24rem), staying in view as the lines
 * scroll. Below `lg`: a slim bar under the page and a sheet behind it. `header` spans both columns.
 */
export function BasketSummaryLayout({
  summary,
  header,
  children,
}: {
  summary: BasketSummaryProps;
  /** Full width above both columns: the heading, and any banner about the page as a whole. */
  header?: ReactNode;
  children: ReactNode;
}) {
  const isSummaryBesidePage = useMediaQuery(SUMMARY_BESIDE_PAGE);
  const panelRef = useRef<HTMLElement | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const { totals } = summary;
  const shared = {
    label: summary.label,
    sheetTitle: summary.sheetTitle,
    state: summary.state ?? null,
    totalsBlock: (
      <div className="space-y-2">
        <p className="text-sm font-medium">Order summary</p>
        <BasketTotals totals={totals} />
      </div>
    ),
    barTotal: {
      text:
        totals.total_inc_vat_pence === null
          ? "—"
          : formatQuotedPrice(totals.total_inc_vat_pence),
      suffix: "inc VAT",
    },
    children: summary.children,
  };
  return (
    <div className={cn("space-y-5", !isSummaryBesidePage && "pb-28")}>
      {header}
      {isSummaryBesidePage ? (
        <div className="grid grid-cols-[minmax(0,3fr)_minmax(24rem,2fr)] items-start gap-8">
          <div className="min-w-0 space-y-5">{children}</div>
          <OrderSummaryPanel {...shared} panelRef={panelRef} isSticky />
        </div>
      ) : (
        <>
          <div className="min-w-0 space-y-5">{children}</div>
          <OrderSummaryBar
            {...shared}
            isSheetOpen={isSheetOpen}
            onSheetOpenChange={setIsSheetOpen}
            barAction={summary.barAction}
          />
        </>
      )}
    </div>
  );
}
